import { useState } from 'react';
import QRCode from 'react-qr-code';

/**
 * Sharing surface for joltstream. Three paths, all on one page:
 *
 *   1. Native share sheet (`navigator.share`) — best UX on mobile;
 *      one tap → the OS share sheet that lets the user fire any
 *      installed app (Messages, Mail, WhatsApp, AirDrop, etc.).
 *      Not available on desktop browsers.
 *
 *   2. QR code — for showing your screen to a person standing
 *      next to you. Works everywhere; no install required to scan.
 *      Phones in the last decade all recognise QR codes in the
 *      built-in camera app.
 *
 *   3. Copy link — universal fallback for desktop browsers and any
 *      device where the user just wants the URL in their clipboard.
 *
 *   4. (Optional) NFC write — Chrome Android only, requires Web NFC
 *      support. Lets you write the URL to an NFC tag. Useful for
 *      sticker on a dashboard / car badge. We hide the button when
 *      the API isn't available rather than showing it disabled.
 *
 * The shared URL is the canonical joltstream.app homepage. Anyone
 * landing there can read the About page, start streaming data, or
 * follow the install link without needing any account or sign-in.
 */

// Type for the Web NFC API — not in lib.dom.d.ts as of 2026-05,
// so we declare the slim surface we use ourselves.
interface NDEFReaderInstance {
  write(message: { records: { recordType: string; data: string }[] }): Promise<void>;
}
interface NDEFReaderConstructor {
  new (): NDEFReaderInstance;
}
interface WindowWithNFC extends Window {
  NDEFReader?: NDEFReaderConstructor;
}

const SHARE_URL = 'https://joltstream.app/';
const SHARE_TITLE = 'joltstream';
const SHARE_TEXT =
  'Anonymous open road data — drive normally and contribute. Join the beta.';

export function SharePage() {
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'error'>('idle');
  const [shareState, setShareState] = useState<'idle' | 'sharing' | 'error'>('idle');
  const [nfcState, setNfcState] = useState<'idle' | 'writing' | 'ok' | 'error'>('idle');

  const nav = navigator as Navigator & {
    share?: (data: { title?: string; text?: string; url?: string }) => Promise<void>;
  };
  const supportsNativeShare = typeof nav.share === 'function';
  const supportsNfc = typeof (window as WindowWithNFC).NDEFReader === 'function';

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(SHARE_URL);
      setCopyState('copied');
    } catch {
      setCopyState('error');
    }
    setTimeout(() => setCopyState('idle'), 2500);
  };

  const onNativeShare = async () => {
    if (!supportsNativeShare) return;
    setShareState('sharing');
    try {
      await nav.share!({
        title: SHARE_TITLE,
        text: SHARE_TEXT,
        url: SHARE_URL,
      });
      setShareState('idle');
    } catch (e) {
      // AbortError is the user cancelling the share sheet — silently
      // return to idle in that case rather than showing an error.
      if (e instanceof Error && e.name === 'AbortError') {
        setShareState('idle');
      } else {
        setShareState('error');
        setTimeout(() => setShareState('idle'), 2500);
      }
    }
  };

  const onWriteNfc = async () => {
    const ctor = (window as WindowWithNFC).NDEFReader;
    if (!ctor) return;
    setNfcState('writing');
    try {
      const reader = new ctor();
      await reader.write({
        records: [{ recordType: 'url', data: SHARE_URL }],
      });
      setNfcState('ok');
    } catch {
      setNfcState('error');
    }
    setTimeout(() => setNfcState('idle'), 3000);
  };

  return (
    <div className="page prose share-page">
      <h1>Share joltstream</h1>
      <p>
        Help us collect more road data — tell someone who drives. No
        account, no signup; whoever you share with can be contributing
        within a minute of opening the link.
      </p>

      <div className="share-grid">
        <section className="share-card share-card-qr">
          <h2>Scan to install</h2>
          <p className="hint">
            Point a camera at the code. Works with any phone's built-in
            camera app — no QR-reader app needed.
          </p>
          <div className="share-qr-wrap">
            <QRCode
              value={SHARE_URL}
              size={220}
              bgColor="#ffffff"
              fgColor="#1a1a1a"
              level="M"
              aria-label={`QR code linking to ${SHARE_URL}`}
            />
          </div>
          <p className="share-qr-caption">
            <code>{SHARE_URL}</code>
          </p>
        </section>

        <section className="share-card share-card-actions">
          <h2>Send a link</h2>
          {supportsNativeShare && (
            <button
              type="button"
              className="primary share-action-button"
              onClick={onNativeShare}
              disabled={shareState === 'sharing'}
            >
              {shareState === 'idle' && 'Share via your device…'}
              {shareState === 'sharing' && 'Opening share sheet…'}
              {shareState === 'error' && '✕ Share failed'}
            </button>
          )}
          <button
            type="button"
            className="share-action-button"
            onClick={onCopy}
          >
            {copyState === 'idle' && 'Copy link'}
            {copyState === 'copied' && '✓ Copied'}
            {copyState === 'error' && '✕ Copy failed'}
          </button>
          <a
            className="share-action-button"
            href={`mailto:?subject=${encodeURIComponent(SHARE_TITLE)}&body=${encodeURIComponent(SHARE_TEXT + '\n\n' + SHARE_URL)}`}
          >
            Email a link
          </a>
          <p className="hint">
            "Email a link" opens your default mail app with the URL
            pre-filled. On a desktop browser without a share sheet,
            this and "Copy link" are usually what you want.
          </p>

          {supportsNfc && (
            <>
              <h2 style={{ marginTop: '1.5rem' }}>Write to NFC tag</h2>
              <p className="hint">
                Got a blank NFC tag (sticker, card, key fob)? Tap your
                phone to it after pressing the button and it'll carry
                the joltstream link from now on.
              </p>
              <button
                type="button"
                className="share-action-button"
                onClick={onWriteNfc}
                disabled={nfcState === 'writing'}
              >
                {nfcState === 'idle' && 'Write to NFC tag'}
                {nfcState === 'writing' && 'Hold tag to phone…'}
                {nfcState === 'ok' && '✓ Written'}
                {nfcState === 'error' && '✕ Write failed'}
              </button>
            </>
          )}
        </section>
      </div>

      <p className="hint" style={{ marginTop: '1.5rem' }}>
        Sharing doesn't tell us anything — the recipient lands on{' '}
        <code>joltstream.app</code> as an anonymous visitor like everyone
        else. There's no referral tracking and there will never be.
      </p>
    </div>
  );
}
